import { ReactNode, Fragment, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import { SkeletonLoader } from '@unified-checkout/design-system';

import {
  REACH_PAGE_EVENTS,
  REACH_PAGE_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/reach';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useLangStore } from '@app/dashboard/store/lang';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { BOXES_CAMPAIGNS_STATICS } from '@app/dashboard/constants/reach/campaigns';
import { EMPTY_VALUE } from '@app/dashboard/constants/shared';

import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';
import {
  formatToISODate,
  formatToLocalizedDateTime,
} from '@app/dashboard/utils/dateFormats';
import { fetchCampaignStatics } from '@app/dashboard/services/inner_layout/reach/campaigns/singleCampaign';

import StaticsBoxes from '../../../../components/StaticsBoxes';

import styles from './Details.module.scss';

const Details = ({
  campaignDetails,
  campaignId,
}: {
  campaignDetails: {
    campaignDetails: any;
    isCampaignDetailsLoading: boolean;
    isCampaignDetailsError: boolean;
  };
  campaignId: string | undefined;
}) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();

  const { data: { data: campaignStatics } = {} } = useQuery({
    queryKey: [QUERY_KEYS.REACH_GET_CAMPAIGN_STATICS, campaignId],
    queryFn: () => fetchCampaignStatics(campaignId as string),
    enabled: !!campaignId,
  });

  const staticsBoxes = useMemo(() => {
    return BOXES_CAMPAIGNS_STATICS(t, campaignStatics);
  }, [t, campaignStatics]);

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const { campaignDetails: campaignSource, isCampaignDetailsLoading } =
    campaignDetails;

  const {
    id,
    title,
    description,
    content,
    channel,
    total_customers,
    start_date,
    approved_date,
    is_terminated,
    is_sent,
    is_approved,
  } = campaignSource;

  const detailsBoxRenderer = ({
    box = undefined,
    height,
    noOfRows,
    key,
  }: {
    box?: ReactNode;
    height: string;
    noOfRows: number;
    key: number | string;
  }): ReactNode => {
    return isCampaignDetailsLoading ? (
      <SkeletonLoader
        type="bordered_box"
        noOfRows={noOfRows}
        height={height}
        key={key}
      />
    ) : (
      box && <Fragment key={key}>{box}</Fragment>
    );
  };

  const allBoxes = [
    detailsBoxRenderer({
      box: (
        <InfoCard
          title={t(
            'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.title'
          )}
          noOfColumns={isMobile ? 1 : 2}
          pairsList={[
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.campaignTitle'
              ),
              value: title || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.campaignId'
              ),
              value: id || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.campaignDescription'
              ),
              value: description || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.campaignContent'
              ),
              value: content || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.approved'
              ),
              value: t(
                is_approved
                  ? `innerLayout.reach.campaigns.table.status.${
                      is_approved === 'True' ? 'yes' : 'no'
                    }`
                  : EMPTY_VALUE
              ),
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.sent'
              ),
              value: t(
                is_sent
                  ? `innerLayout.reach.campaigns.table.status.${
                      is_sent === 'True' ? 'yes' : 'no'
                    }`
                  : EMPTY_VALUE
              ),
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.campaignStatus'
              ),
              value: t(
                typeof is_terminated === 'boolean'
                  ? `innerLayout.reach.campaigns.table.status.${
                      is_terminated ? 'hold' : 'active'
                    }`
                  : EMPTY_VALUE
              ),
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.campaignChannel'
              ),
              value: channel || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.totalCustomers'
              ),
              value: total_customers || EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.startDate'
              ),
              value: approved_date
                ? formatToLocalizedDateTime(start_date, lang)
                : EMPTY_VALUE,
            },
            {
              label: t(
                'innerLayout.reach.campaigns.singleCampaignPage.detailsBox.approvedDate'
              ),
              value: approved_date
                ? formatToLocalizedDateTime(approved_date, lang)
                : EMPTY_VALUE,
            },
          ]}
          width="100%"
        />
      ),
      noOfRows: 7,
      height: '28rem',
      key: t('innerLayout.reach.campaigns.singleCampaignPage.detailsBox.title'),
    }),
  ];

  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const renderContent = (
    <>
      {isMobile ? (
        <>
          {col1Boxes}
          {col2Boxes}
        </>
      ) : (
        [col1Boxes, col2Boxes].map((colContent, index) => (
          <div
            className={styles['single-campaign__details-col']}
            key={`single-campaign__details-col-${index}`}
          >
            {colContent}
          </div>
        ))
      )}
    </>
  );

  useEffect(() => {
    const hasData = [campaignSource, campaignStatics].every(
      (item) => !!Object.keys(item || {}).length
    );    

    if (!hasData) return;

    const { failed_count, success_count, total_sms_count } =
      campaignStatics?.data?.data || {};

    const eventName = REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CAMPAIGN_DETAILS_VIEWED;

    const props =
      REACH_PAGE_EVENTS_PROPERTIES.CAMPAIGNS_PAGE.CAMPAIGN_DETAILS_VIEWED;

    const payload = {
      [props.CAMPAIGN_STATUS]: is_terminated ? 'terminated' : 'active',
      [props.CAMPAIGN_ID]: Number(id),
      [props.IS_APPROVED]: is_approved === 'True',
      [props.CAMPAIGN_CHANNEL]: channel,
      [props.IS_SENT]: is_sent === 'True',
      [props.TOTAL_CUSTOMERS]: total_customers,
      [props.START_DATE]: formatToISODate(start_date, 'dd MMM yyyy, hh:mm a'),
      [props.APPROVED_DATE]: formatToISODate(
        approved_date,
        'dd MMM yyyy, hh:mm a'
      ),
      [props.SMS_COUNT]: total_sms_count || 0,
      [props.SUCCESS_SMS]: success_count || 0,
      [props.FAIL_SMS]: failed_count || 0,
    };

    amplitude.track(eventName, payload);
  }, [campaignSource, campaignStatics]);

  return (
    <div className={styles['single-campaign__details']}>
      <section className={styles['single-campaign__details-statics']}>
        <div className={styles['single-campaign__details-statics-content']}>
          <h2
            className={styles['single-campaign__details-statics-content-title']}
          >
            {t('innerLayout.reach.campaigns.singleCampaignPage.statics.title')}
          </h2>
          <p
            className={
              styles['single-campaign__details-statics-content-subtitle']
            }
          >
            {t(
              'innerLayout.reach.campaigns.singleCampaignPage.statics.subTitle'
            )}
          </p>
        </div>

        <StaticsBoxes
          isLoading={isCampaignDetailsLoading}
          campaignStaticsList={staticsBoxes}
        />
      </section>

      {renderContent}
    </div>
  );
};

export default Details;
